'use client';

import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useNotifier } from '../../../components/Notifier';
import DataTable from '../../../components/DataTable';
import {
  Box,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Stack,
  TextField,
  Popover,
  CircularProgress,
  LinearProgress,
  Divider
} from '@mui/material';
import { Dialog, DialogContent, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { apiHowen, apiVss } from '../../../api/axiosClient';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import SmartDisplayOutlinedIcon from '@mui/icons-material/SmartDisplayOutlined';
import FileDownloadOffOutlinedIcon from '@mui/icons-material/FileDownloadOffOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useLanguage } from '../../../utils/i18n';
import { VSS_CONFIG } from '../../../config/constants';

import Breadcrumbs from '../../../components/@extended/Breadcrumbs';
import InformationBubbleDialog from '../../../components/InformationBubbleDialog';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';

// const formatDateForApi = (dateStr, isEnd = false) => {
//   if (!dateStr) return '';
//   return isEnd ? `${dateStr} 23:59:59` : `${dateStr} 00:00:00`;
// };

const MENU_PROPS = {
  PaperProps: {
    style: {
      maxHeight: 48 * 4.5 + 8,
      width: 250
    }
  }
};

function replaceIpWithDomain(url) {
  if (!url) return url;
  // Match any IP optionally with port and replace with the configured HOST
  return url.replace(/[0-9.]+(:\d+)?/g, (match) => {
    // Basic IP shape check
    if (match.startsWith('103.169') || match.match(/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/)) {
      return VSS_CONFIG.HOST;
    }
    return match;
  });
}

export default function HistoryDownloadVideoPage() {
  const { t } = useLanguage();
  const notify = useNotifier();
  const location = useLocation();
  const navigate = useNavigate();
  const [previewVideoUrl, setPreviewVideoUrl] = useState(null);
  const [token, setToken] = useState(null);
  const [downloadTask, setDownloadTask] = useState(null);
  const [anchorElDownload, setAnchorElDownload] = useState(null);
  const downloadTimerRef = useRef(null);
  const vehicleListFromState = useMemo(() => location.state?.vehicleList || [], [location.state]);
  const [tableSearchQuery, setTableSearchQuery] = useState('');
  const [vehicleSearchText, setVehicleSearchText] = useState('');
  const [openInfo, setOpenInfo] = useState(false);

  const vehicleOptions = useMemo(() => {
    return vehicleListFromState
      .map((v) => {
        const imei = v.raw?.integration_imei || v.integration_imei || v.info?.integration_imei || v.deviceID || '';
        const label = v.vehicle_license_plate || v.plate_number || v.vehicle_name || v.fleet_name || String(v.id || '');
        const cp =
          v.raw?.join?.company_name ||
          v.join?.company_name ||
          v.raw?.company_name ||
          v.company_name ||
          v.raw?.info?.company_name ||
          v.info?.company_name ||
          v.company?.name ||
          '-';

        return {
          value: String(imei).trim(),
          label: String(label).trim(),
          cp: String(cp).trim()
        };
      })
      .filter((o) => o.value && o.value !== 'null' && o.value !== 'undefined');
  }, [vehicleListFromState]);

  const filteredVehicleOptions = useMemo(() => {
    if (!vehicleSearchText) return vehicleOptions;
    return vehicleOptions.filter((v) => v.label.toLowerCase().includes(vehicleSearchText.toLowerCase()));
  }, [vehicleOptions, vehicleSearchText]);

  const visibleIds = useMemo(() => filteredVehicleOptions.map((v) => v.value), [filteredVehicleOptions]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const isAllVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedDeviceIds.includes(id));
  const isSomeVisibleSelected = visibleIds.some((id) => selectedDeviceIds.includes(id));
  const isIndeterminateVehicles = isSomeVisibleSelected && !isAllVisibleSelected;

  const handleVehicleChange = (e) => {
    const val = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
    if (val.includes('select-all-option')) {
      if (isAllVisibleSelected) {
        setSelectedDeviceIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
      } else {
        setSelectedDeviceIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
      }
    } else {
      setSelectedDeviceIds(val);
    }
  };
  const [rangeOption, setRangeOption] = useState('today');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  useEffect(() => {
    if (!rangeOption) return;
    if (rangeOption === 'custom' && fromDate && toDate) return;

    const now = dayjs();
    if (rangeOption === 'custom') {
      if (!fromDate) {
        setFromDate('');
        setToDate('');
      }
      return;
    }

    let from;
    let to = now;

    if (rangeOption === 'today') {
      from = now.startOf('day');
    } else if (rangeOption === 'week') {
      from = now.subtract(7, 'day').startOf('day');
      to = now.endOf('day');
    } else if (rangeOption === 'month') {
      from = now.subtract(1, 'month').startOf('day');
      to = now.endOf('day');
    } else {
      return;
    }

    if (from && to) {
      setFromDate(from.format('YYYY-MM-DD HH:mm:ss'));
      setToDate(to.format('YYYY-MM-DD HH:mm:ss'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeOption]);
  // const [tableSearchQuery, setTableSearchQuery] = useState('');
  const [searchField, setSearchField] = useState('license_plate');

  const searchOptions = [
    { label: t('liveStreaming.plateNumber', 'Plate Number'), value: 'license_plate' },
    { label: t('liveStreaming.vehicleType', 'Vehicle Type'), value: 'vehicle_type' },
    { label: t('liveStreaming.camera', 'Camera'), value: 'camera' },
    { label: t('liveStreaming.username', 'Username'), value: 'username' }
  ];

  const [deleteDialog, setDeleteDialog] = useState({ open: false, row: null, loading: false });

  const fetchList = useCallback(async () => {
    const hasVehicles = selectedDeviceIds.length > 0;
    const hasDates = !!fromDate && !!toDate;
    if (!hasVehicles || !hasDates) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      let fetchedToken = null;
      try {
        const tk = await apiHowen.get('/get-token');
        fetchedToken = tk?.data?.token ?? tk?.data?.data?.token ?? tk?.data ?? null;
        if (fetchedToken) setToken(String(fetchedToken));
      } catch (e) {
        console.log('Token fetch error', e);
      }
      const pageNum = 1;
      const pageCount = 100;

      let apiChunks = [];
      const localStart = dayjs(fromDate);
      const localEnd = dayjs(toDate);
      const pType = rangeOption;
      const isFullDayPeriod = ['week', 'last_week', 'month', 'last_month'].includes(pType);

      if (isFullDayPeriod) {
        let curr = localStart.startOf('day');
        const end = localEnd.startOf('day');
        while (curr.isBefore(end) || curr.isSame(end, 'day')) {
          let dStart = curr.startOf('day');
          let dEnd = curr.endOf('day');
          apiChunks.push({
            start: dStart.format('YYYY-MM-DD HH:mm:ss'),
            end: dEnd.format('YYYY-MM-DD HH:mm:ss')
          });
          curr = curr.add(1, 'day');
        }
      } else {
        // Chunk by local days for this specific API which expects local time strings
        let curr = localStart.startOf('day');
        const end = localEnd.startOf('day');

        while (curr.isBefore(end) || curr.isSame(end, 'day')) {
          let chunkStart = curr.startOf('day');
          let chunkEnd = curr.endOf('day');

          if (curr.isSame(localStart, 'day')) chunkStart = localStart;
          if (curr.isSame(localEnd, 'day')) chunkEnd = localEnd;

          apiChunks.push({
            start: chunkStart.format('YYYY-MM-DD HH:mm:ss'),
            end: chunkEnd.format('YYYY-MM-DD HH:mm:ss')
          });

          curr = curr.add(1, 'day');
        }
      }

      const requests = [];
      selectedDeviceIds.forEach((deviceId) => {
        apiChunks.forEach((chunk) => {
          const apiBeginTime = chunk.start;
          const apiEndTime = chunk.end;

          const payload = {
            pageNum,
            pageCount,
            taskState: null,
            taskStartTime: apiBeginTime,
            taskEndTime: apiEndTime,
            deviceNo: deviceId,
            username: '',
            scheme: 'http',
            ...(fetchedToken && { token: String(fetchedToken) })
          };

          requests.push(
            apiVss.post('/webdownrecord/findPage.action', payload, {
              params: {
                pageNum,
                pageCount,
                refreshToken: false,
                token: fetchedToken,
                scheme: 'http',
                lang: 'en_US'
              },
              headers: { 'Content-Type': 'application/json' }
            })
          );
        });
      });

      const responses = await Promise.all(requests);
      let combinedList = [];

      responses.forEach((resp) => {
        const body = resp?.data ?? resp;
        if (body?.status === 10000 && body?.data) {
          const list = body.data.list || [];
          combinedList = [...combinedList, ...list];
        }
      });

      const mapped = combinedList.map((item, i) => {
        const foundVehicle = vehicleListFromState.find((v) => {
          const vImei = v.raw?.integration_imei || v.integration_imei || v.info?.integration_imei || v.deviceID || '';
          return String(vImei).trim() === String(item.deviceNo).trim();
        });

        const extractTime = (dateTimeStr) => {
          if (!dateTimeStr || dateTimeStr === '-') return '-';
          const parts = dateTimeStr.split(' ');
          return parts.length > 1 ? parts[1] : dateTimeStr;
        };
        const rawVideoUrl = item.downUrl || '';
        const finalVideoUrl = replaceIpWithDomain(rawVideoUrl);

        return {
          id: item.guid || `${item.deviceNo}-${i}`,
          license_plate: foundVehicle?.Join?.vehicle_license_plate || foundVehicle?.vehicle_license_plate || item.deviceNo || '-',
          vehicle_type: foundVehicle?.Join?.vehicle_type_name || foundVehicle?.vehicle_type_name || '-',
          vehicle_name: foundVehicle?.Join?.vehicle_name || foundVehicle?.vehicle_name || '-',
          device_imei: item.deviceNo,
          camera: item.channel ? `Channel ${item.channel}` : '-',
          resStartTime: extractTime(item.resStartTime),
          resEndTime: extractTime(item.resEndTime),
          taskCreateTime: item.taskCreateTime || '-',
          username: item.username || '-',
          video: finalVideoUrl,
          taskState: item.taskState,
          rawItem: item
        };
      });

      const sorted = mapped.sort((a, b) => dayjs(b.taskCreateTime).unix() - dayjs(a.taskCreateTime).unix());

      setRows(sorted);
    } catch (err) {
      setError(String(err?.message ?? err));
      setRows([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, selectedDeviceIds, vehicleListFromState]);

  const handleGenerateReport = () => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));

    fetchList();
  };
  const filteredRows = useMemo(() => {
    if (!tableSearchQuery) return rows;

    const query = tableSearchQuery.toLowerCase().trim();

    return rows.filter((row) => {
      const valueToSearch = row[searchField] ? String(row[searchField]).toLowerCase() : '';
      return valueToSearch.includes(query);
    });
  }, [rows, tableSearchQuery, searchField]);

  const handleViewVideo = (url) => {
    if (!url) return alert('Video URL tidak tersedia');
    setPreviewVideoUrl(url);
  };
  const handleDownloadFile = (url) => {
    if (!url) return alert('Link download tidak tersedia');
    const link = document.createElement('a');
    link.href = url;
    const fileName = url.split('/').pop() || 'video_download.mp4';
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const startPollingProgress = useCallback(
    (taskId, rowData, resWebPath) => {
      if (downloadTimerRef.current) clearInterval(downloadTimerRef.current);

      downloadTimerRef.current = setInterval(async () => {
        try {
          const res = await apiVss.post(
            '/webdownrecord/findTaskProgress.action',
            {
              taskId: taskId,
              token: token,
              resWebPath: resWebPath
            },
            { headers: { 'Content-Type': 'application/json' } }
          );

          const data = res.data;

          if (data && data.status === 10000) {
            const progress = data.data.progress || 0;
            const state = data.data.downState;
            setDownloadTask((prev) => ({
              ...prev,
              progress: progress,
              stateCode: state
            }));

            if (progress >= 100 || state === 2) {
              clearInterval(downloadTimerRef.current);
              const rawFinalUrl = data.data.downUrl || rowData.video;
              const finalUrl = replaceIpWithDomain(rawFinalUrl);
              setDownloadTask((prev) => ({
                ...prev,
                status: 'finished',
                progress: 100,
                resultUrl: finalUrl
              }));
              fetchList(); // Refresh list to get updated status
            } else if (state === -1 || state === -9 || state < 0) {
              clearInterval(downloadTimerRef.current);
              const rawFinalUrl = data.data.downUrl || rowData.video;
              const finalUrl = replaceIpWithDomain(rawFinalUrl);
              let errorMsg = 'Download Failed/Stopped on Server';
              if (state === -1) errorMsg = 'Pause state';
              else if (state === -7) errorMsg = 'Device no data';
              else if (state === -8) errorMsg = 'Device not online';
              else if (state === -9) errorMsg = 'Abnormal stop state';

              setDownloadTask((prev) => ({
                ...prev,
                status: 'error',
                errorMsg: errorMsg,
                resultUrl: finalUrl
              }));
            }
          }
        } catch (err) {
          console.error('Polling error', err);
        }
      }, 1500);
    },
    [token, fetchList]
  );

  const handleInitDownload = useCallback(
    async (event, row) => {
      event.stopPropagation();
      setAnchorElDownload(event.currentTarget);
      setDownloadTask({ status: 'running', progress: 0, taskId: null, fileData: row });
      try {
        const payloadInsert = {
          username: 'admin',
          deviceNo: row.device_imei,
          channel: String(row.rawItem?.channel || 1).replace(/\D/g, ''),
          resStartTime: row.rawItem?.resStartTime,
          resEndTime: row.rawItem?.resEndTime,
          resDeviceFileName: '',
          fileFormat: 'mp4',
          scheme: 'https',
          token: token,
          lang: 'en_US'
        };

        const res = await apiVss.post('/webdownrecord/insert.action', payloadInsert, {
          headers: { 'Content-Type': 'application/json' }
        });
        const data = res.data;

        if (data && data.status === 10000 && data.data?.taskId) {
          const taskId = data.data.taskId;
          const rawPath = data.data.resWebPath;
          try {
            const payloadTask = {
              taskId: taskId,
              token: token,
              action: 1,
              username: 'admin',
              resWebPath: rawPath
            };

            await apiVss.post('/webdownrecord/taskManagement.action', payloadTask, {
              headers: { 'Content-Type': 'application/json' }
            });
          } catch (e) {
            console.warn('Trigger Resume failed', e);
          }
          setDownloadTask((prev) => ({ ...prev, taskId: taskId, progress: 1 }));
          startPollingProgress(taskId, row, rawPath);
        } else {
          throw new Error(data?.msg || 'Gagal membuat tugas unduhan');
        }
      } catch (err) {
        console.error('Download init error', err);
        setDownloadTask((prev) => ({ ...prev, status: 'error', errorMsg: err.message }));
      }
    },
    [token, startPollingProgress]
  );

  useEffect(() => {
    return () => {
      if (downloadTimerRef.current) clearInterval(downloadTimerRef.current);
    };
  }, []);

  const handleCloseDownloadPopover = () => {
    if (downloadTimerRef.current) clearInterval(downloadTimerRef.current);
    setAnchorElDownload(null);
    setDownloadTask(null);
  };

  const handleDeleteTask = async () => {
    const row = deleteDialog.row;
    if (!row) return;

    setDeleteDialog((prev) => ({ ...prev, loading: true }));
    try {
      const raw = row.rawItem || row.fileData || {};
      let actualTaskId = raw.guid || raw.taskId || row.id;
      if (String(actualTaskId) === '0' && raw.guid) {
        actualTaskId = raw.guid;
      }

      const payload = {
        taskId: String(actualTaskId),
        action: -1,
        resWebPath: raw.resWebPath || raw.taskFilewebpath || raw.downUrl || '',
        username: raw.username || 'admin',
        token: String(token)
      };

      const res = await apiVss.post('/webdownrecord/taskManagement.action', payload, {
        headers: { 'Content-Type': 'application/json' }
      });

      if (res.data && res.data.status === 10000) {
        notify({ message: 'Download task successfully deleted', severity: 'success' });
        fetchList();
      } else {
        notify({ message: res.data?.message || 'Failed to delete download task', severity: 'error' });
      }
    } catch {
      notify({ message: 'An error occurred while deleting the task', severity: 'error' });
    } finally {
      setDeleteDialog({ open: false, row: null, loading: false });
    }
  };

  const handleFinalDownload = async () => {
    if (!downloadTask?.resultUrl) return;
    handleDownloadFile(downloadTask.resultUrl);
    handleCloseDownloadPopover();
  };

  const columns = useMemo(
    () => [
      { field: 'license_plate', headerName: t('liveStreaming.plateNumber', 'Plate Number'), width: 150 },
      { field: 'vehicle_type', headerName: t('liveStreaming.vehicleType', 'Vehicle Type'), width: 150 },
      { field: 'vehicle_name', headerName: t('liveStreaming.vehicleCategory', 'Vehicle Category'), width: 150 },
      { field: 'camera', headerName: t('liveStreaming.camera', 'Camera'), width: 90 },
      { field: 'resStartTime', headerName: t('liveStreaming.startVideo', 'Start Video'), width: 110 },
      { field: 'resEndTime', headerName: t('liveStreaming.endVideo', 'End Video'), width: 110 },
      {
        field: 'taskCreateTime',
        headerName: t('liveStreaming.downloadDate', 'Download Date'),
        width: 170,
        valueGetter: (value, params) => {
          if (!params.taskCreateTime || params.taskCreateTime === '-') return '-';
          return dayjs(params.taskCreateTime).format('DD/MM/YY HH:mm:ss');
        }
      },
      { field: 'username', headerName: t('liveStreaming.username', 'Username'), width: 120 },
      {
        field: 'video',
        headerName: t('liveStreaming.video', 'Video'),
        width: 120,
        renderCell: (params) => {
          const file = params.row.video;
          const state = params.row.taskState;

          return (
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              {state === 2 && file ? (
                <>
                  <IconButton size="small" onClick={() => handleViewVideo(file)} title={t('buttonTitle.view', 'View Video')} color="info">
                    <SmartDisplayOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDownloadFile(file)}
                    title={t('buttonTitle.download', 'Download Video')}
                    color="primary"
                  >
                    <SaveAltIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <>
                  {/* <Chip
                    label="Unfinished"
                    size="small"
                    color="default"
                    variant="outlined"
                    sx={{ height: 24, fontSize: '0.7rem', fontWeight: 600 }}
                  /> */}
                  <IconButton
                    size="small"
                    title="Unfinished"
                    color="default"
                    onClick={() => {
                      notify({ message: 'The video file is corrupted or incomplete. Please redownload the video.', severity: 'error' });
                    }}
                  >
                    <FileDownloadOffOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="default" title="Redownload Video" onClick={(e) => handleInitDownload(e, params.row)}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </>
              )}
              <IconButton
                size="small"
                color="error"
                title="Delete Task"
                onClick={() => setDeleteDialog({ open: true, row: params.row, loading: false })}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        }
      }
    ],
    [t, notify, handleInitDownload]
  );

  return (
    <Box>
      <Breadcrumbs
        custom
        heading={t('liveStreaming.historyDownloadVideo', 'History Download Video')}
        links={[
          { title: 'Dashboard', to: '/dashboard' },
          { title: 'Live Streaming', to: '/live-streaming?name=live-streaming' },
          { title: t('liveStreaming.historyDownloadVideo', 'History Download Video') }
        ]}
      />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Button
            startIcon={<ArrowBackIcon fontSize="small" />}
            onClick={() => navigate('/live-streaming?name=live-streaming')}
            size="small"
            sx={{
              color: 'primary',
              textTransform: 'none',
              padding: 0,
              minWidth: 0,
              '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
            }}
          >
            {t('buttonTitle.back', 'Back')}
          </Button>
        </Box>
        <Box sx={{ height: 'calc(100vh - 150px)', display: 'flex', flexDirection: 'column' }}>
          <Paper sx={{ p: 3, mb: 1, borderRadius: 1, flexShrink: 0 }}>
            <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
              <Box sx={{ flex: '1 1 260px' }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="veh-label">{t('liveStreaming.selectVehicle', 'Select Vehicle')}</InputLabel>
                  <Select
                    labelId="veh-label"
                    multiple
                    value={selectedDeviceIds}
                    onChange={handleVehicleChange}
                    input={<OutlinedInput label={t('liveStreaming.selectVehicle', 'Select Vehicle')} />}
                    renderValue={(selected) => {
                      if (selected.length === 0) return <em>{t('liveStreaming.selectVehicle', 'Select Vehicle')}</em>;
                      return selected
                        .map((id) => {
                          const found = vehicleOptions.find((o) => o.value === id);
                          return found ? found.label : id;
                        })
                        .join(', ');
                    }}
                    MenuProps={MENU_PROPS}
                  >
                    <MenuItem disableRipple>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Cari kendaraan..."
                        value={vehicleSearchText}
                        onChange={(e) => setVehicleSearchText(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                      />
                    </MenuItem>
                    <MenuItem value="select-all-option">
                      <Checkbox checked={isAllVisibleSelected} indeterminate={isIndeterminateVehicles} />
                      <ListItemText primary="Select All" />
                    </MenuItem>
                    {filteredVehicleOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        <Checkbox checked={selectedDeviceIds.indexOf(opt.value) > -1} />
                        <ListItemText
                          primary={opt.label}
                          secondary={
                            <Typography variant="caption" sx={{ color: '#888' }}>
                              {opt.cp}
                            </Typography>
                          }
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <FormControl
                  size="small"
                  sx={{
                    minWidth: 160,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#485D9A' },
                      '&:hover fieldset': { borderColor: '#485D9A' },
                      '&.Mui-focused fieldset': { borderColor: '#485D9A' }
                    },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#485D9A' }
                  }}
                >
                  <InputLabel id="range-select-label">{t('reportFatigue.periodRange', 'Periode Range')}</InputLabel>
                  <Select
                    labelId="range-select-label"
                    value={rangeOption}
                    label={t('reportFatigue.periodRange', 'Periode Range')}
                    onChange={(e) => setRangeOption(e.target.value)}
                  >
                    <MenuItem value="today">Today</MenuItem>
                    <MenuItem value="week">{t('reportFatigue.lastWeek', 'Last Week')}</MenuItem>
                    <MenuItem value="month">{t('reportFatigue.lastMonth', 'Last Month')}</MenuItem>
                    <MenuItem value="custom">{t('reportFatigue.custom', 'Custom')}</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {rangeOption === 'custom' && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <DateTimePicker
                    label={t('reportFatigue.from', 'From')}
                    format="DD/MM/YYYY HH:mm:ss"
                    ampm={false}
                    timeSteps={{ minutes: 1, seconds: 1 }}
                    value={fromDate ? dayjs(fromDate) : null}
                    maxDate={toDate ? dayjs(toDate) : undefined}
                    onChange={(v) => setFromDate(v ? v.format('YYYY-MM-DD HH:mm:ss') : '')}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: {
                          minWidth: 160,
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#485D9A' },
                            '&:hover fieldset': { borderColor: '#485D9A' },
                            '&.Mui-focused fieldset': { borderColor: '#485D9A' }
                          },
                          '& .MuiInputLabel-root.Mui-focused': { color: '#485D9A' }
                        }
                      }
                    }}
                  />
                  <DateTimePicker
                    label={t('reportFatigue.to', 'To')}
                    format="DD/MM/YYYY HH:mm:ss"
                    ampm={false}
                    timeSteps={{ minutes: 1, seconds: 1 }}
                    value={toDate ? dayjs(toDate) : null}
                    minDate={fromDate ? dayjs(fromDate) : undefined}
                    onChange={(v) => setToDate(v ? v.format('YYYY-MM-DD HH:mm:ss') : '')}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: {
                          minWidth: 160,
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#485D9A' },
                            '&:hover fieldset': { borderColor: '#485D9A' },
                            '&.Mui-focused fieldset': { borderColor: '#485D9A' }
                          },
                          '& .MuiInputLabel-root.Mui-focused': { color: '#485D9A' }
                        }
                      }
                    }}
                  />
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    setSelectedDeviceIds([]);
                    setRangeOption('today');
                    setRows([]);
                  }}
                >
                  {t('general.reset', 'Reset')}
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SearchIcon />}
                  onClick={handleGenerateReport}
                  disabled={loading || selectedDeviceIds.length === 0 || !fromDate || !toDate}
                  sx={{ px: 3, fontWeight: 'bold' }}
                >
                  {loading ? t('masterRoute.searching', 'Searching...') : t('general.search', 'Search')}
                </Button>
              </Box>
              <IconButton onClick={() => setOpenInfo(true)} sx={{ bgcolor: '#e3f2fd', color: '#0374DA', borderRadius: 2 }}>
                <InfoOutlinedIcon />
              </IconButton>
            </Box>
          </Paper>
          <Box sx={{ flexGrow: 1, minHeight: 0, '& > div': { height: '100%', display: 'flex', flexDirection: 'column' } }}>
            <DataTable
              rows={filteredRows}
              columns={columns}
              loading={loading}
              hideAddButton
              hideExportButton
              paginationMode="client"
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              searchQuery={tableSearchQuery}
              onSearchChange={(e) => {
                setTableSearchQuery(e.target.value);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
              searchFieldOptions={searchOptions}
              selectedSearchField={searchField}
              onSearchFieldChange={(val) => {
                setSearchField(val);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
              searchLabel={t('buttonTitle.searchBy', 'Search by') + ' ' + (searchOptions.find((o) => o.value === searchField)?.label || '')}
            />
          </Box>
          {error && <Box sx={{ color: 'error.main', mt: 1 }}>{error}</Box>}
        </Box>
        <Dialog open={Boolean(previewVideoUrl)} onClose={() => setPreviewVideoUrl(null)} maxWidth="md" fullWidth>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, pb: 0 }}>
            <Typography variant="h6">{t('liveStreaming.previewVideo', 'Preview Video')}</Typography>
            <IconButton onClick={() => setPreviewVideoUrl(null)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <DialogContent>
            {previewVideoUrl && (
              <video controls autoPlay style={{ width: '100%', maxHeight: '70vh' }}>
                <source src={previewVideoUrl} type="video/mp4" />
                {t('liveStreaming.browserNotSupportVideo', 'Browser tidak mendukung video tag.')}
              </video>
            )}
          </DialogContent>
        </Dialog>
        <InformationBubbleDialog
          open={openInfo}
          onClose={() => setOpenInfo(false)}
          title={t('answer.titleHistoryDownload', 'Why is it Unfinished?')}
          messages={[
            { type: 'q', text: t('question.historyDownload1', 'Why are some of my videos marked as Unfinished?') },
            {
              type: 'a',
              text: t(
                'answer.historyDownload1',
                'An Unfinished status means that the previous video download process was interrupted or failed before it could complete.'
              )
            },
            { type: 'q', text: t('question.historyDownload2', 'Why did it fail?') },
            {
              type: 'a',
              text: t(
                'answer.historyDownload2',
                'Usually, this happens because the camera device on the vehicle was turned off, lost power, or lost internet signal while the download was running.'
              )
            },
            { type: 'q', text: t('question.historyDownload3', 'How do I prevent this?') },
            {
              type: 'a',
              text: t(
                'answer.historyDownload3',
                'Please ensure that the vehicle engine is turned on and the device has an active internet connection when initiating a download.'
              )
            },
            { type: 'q', text: t('question.historyDownload4', 'Can I download the unfinished file?') },
            {
              type: 'a',
              text: t(
                'answer.historyDownload4',
                'No, unfinished video files cannot be downloaded because the file might be corrupted or incomplete. Please perform a Redownload Video instead.'
              )
            },
            { type: 'q', text: t('question.historyDownload5', 'Where can I view successful downloads?') },
            {
              type: 'a',
              text: t(
                'answer.historyDownload5',
                'You can view the successfully downloaded videos here in the History Download Video Page, as well as in the History Today section.'
              )
            }
          ]}
        />
      </LocalizationProvider>

      <Popover
        open={Boolean(anchorElDownload)}
        anchorEl={anchorElDownload}
        onClose={handleCloseDownloadPopover}
        anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
        transformOrigin={{ vertical: 'center', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, width: 300 }}>
          <Typography variant="h6" gutterBottom>
            {t('liveStreaming.downloadStatus', 'Status Unduhan')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {downloadTask?.status === 'running' && (
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={40} sx={{ mb: 1 }} />
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                {downloadTask.stateCode === -8
                  ? t('liveStreaming.waitingForVideo', 'Waiting for the Device to Come Online...')
                  : downloadTask.stateCode === -7
                    ? t('liveStreaming.dataNotFound', 'Data Not Found on Device')
                    : downloadTask.stateCode === 0
                      ? t('liveStreaming.waitingInLine', 'Waiting in Line...')
                      : t('liveStreaming.downloading', 'Downloading from Device...')}
              </Typography>

              {downloadTask.stateCode === -8 && (
                <Typography variant="caption" color="error" display="block" sx={{ mb: 1 }}>
                  {t('liveStreaming.makeSure', 'Make sure the vehicle is on and has a signal.')}
                </Typography>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Box sx={{ width: '100%', mr: 1 }}>
                  <LinearProgress variant="determinate" value={downloadTask.progress} />
                </Box>
                <Box sx={{ minWidth: 35 }}>
                  <Typography variant="body2" color="text.secondary">{`${Math.round(downloadTask.progress)}%`}</Typography>
                </Box>
              </Box>
            </Box>
          )}
          {downloadTask?.status === 'finished' && (
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircleIcon color="success" sx={{ fontSize: 50, mb: 1 }} />
              <Typography variant="body1" gutterBottom>
                {t('liveStreaming.videoReady', 'Video is ready to download!')}
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<PlayCircleOutlineIcon />}
                  onClick={() => {
                    setPreviewVideoUrl(downloadTask.resultUrl);
                    handleCloseDownloadPopover();
                  }}
                >
                  {t('buttonTitle.view', 'Lihat')}
                </Button>
                <Button variant="contained" startIcon={<SaveAltIcon />} onClick={handleFinalDownload}>
                  {t('buttonTitle.download', 'Unduh')}
                </Button>
              </Stack>
            </Box>
          )}
          {downloadTask?.status === 'error' && (
            <Box sx={{ textAlign: 'center' }}>
              <ErrorOutlineIcon color="error" sx={{ fontSize: 50, mb: 1 }} />
              <Typography variant="body1">{t('liveStreaming.failedPrepareVideo', 'Gagal Menyiapkan Video')}</Typography>
              <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                {downloadTask.errorMsg}
              </Typography>
            </Box>
          )}
        </Box>
      </Popover>

      <DeleteConfirmDialog
        open={deleteDialog.open}
        loading={deleteDialog.loading}
        onClose={() => setDeleteDialog({ open: false, row: null, loading: false })}
        onConfirm={handleDeleteTask}
        title="Delete Download Task"
        message="Are you sure you want to delete this video download task and its file?"
        itemName={
          deleteDialog.row?.camera
            ? `${deleteDialog.row.camera} (${deleteDialog.row.resStartTime || ''} - ${deleteDialog.row.resEndTime || ''})`
            : ''
        }
      />
    </Box>
  );
}
